import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Compass, Target, X } from "lucide-react";
import { t } from "@/i18n";

export const DASHBOARD_TUTORIAL_VERSION = "dashboard-tour-v5";

// Cible à la fois la version desktop et mobile avec une virgule (sélecteur CSS multiple)
const STEPS = [
  {
    target: "[data-tour='dashboard-overview'], [data-tour='dashboard-actions']",
    icon: LayoutDashboard,
    titleKey: "dashboard_tutorial.tableau_de_bord_titre",
    textKey: "dashboard_tutorial.tableau_de_bord_texte",
  },
  {
    target: "[data-testid='nav-roadmap'], [data-testid='mobile-nav-roadmap']",
    icon: Compass,
    titleKey: "dashboard_tutorial.apprendre_titre",
    textKey: "dashboard_tutorial.apprendre_texte",
  },
  {
    target: "[data-testid='nav-practice'], [data-testid='mobile-nav-practice']",
    icon: Target,
    titleKey: "dashboard_tutorial.sentrainer_titre",
    textKey: "dashboard_tutorial.sentrainer_texte",
  },
];

function getRect(target) {
  if (!target) return null;
  const rect = target.getBoundingClientRect();
  const padding = 8;
  return {
    left: Math.max(4, rect.left - padding),
    top: Math.max(4, rect.top - padding),
    right: Math.min(window.innerWidth - 4, rect.right + padding),
    bottom: Math.min(window.innerHeight - 4, rect.bottom + padding),
  };
}

function Spotlight({ rect }) {
  if (!rect)
    return (
      <div className="fixed inset-0 z-[60] bg-black/65 pointer-events-none" />
    );
  return (
    <div
      className="fixed z-[61] rounded-xl border-2 border-primary pointer-events-none"
      style={{
        left: rect.left,
        top: rect.top,
        width: rect.right - rect.left,
        height: rect.bottom - rect.top,
        boxShadow:
          "0 0 0 3px rgba(255,255,255,0.9), 0 0 30px rgba(99,102,241,0.8), 0 0 0 100vmax rgba(0,0,0,0.65)",
      }}
    />
  );
}

export default function Tutorial({ onClose }) {
  const { refresh } = useAuth();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const S = STEPS[i];
  const Icon = S.icon;

  useEffect(() => {
    const getVisibleTarget = () => {
      const elements = document.querySelectorAll(S.target);
      for (const el of elements) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          return el;
        }
      }
      return null;
    };

    const target = getVisibleTarget();
    if (!target) {
      setRect(null);
      return undefined;
    }

    const update = () => {
      const currentTarget = getVisibleTarget();
      setRect(getRect(currentTarget));
    };

    update();
    const timer = window.setTimeout(update, 300);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [S]);

  const finish = () => {
    localStorage.removeItem("replayTutorial");
    localStorage.setItem(
      "harf_dashboard_tutorial_version",
      DASHBOARD_TUTORIAL_VERSION,
    );
    onClose();
    api
      .post("/account/tutorial-seen")
      .then(() => refresh())
      .catch(() => {});
  };

  return (
    <div
      className="fixed inset-0 z-[60]"
      data-testid="tutorial"
      role="dialog"
      aria-modal="true"
    >
      <Spotlight rect={rect} />
      <div className="fixed left-1/2 top-1/2 z-[70] w-[min(92vw,26rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-card p-5 shadow-2xl">
        <button
          data-testid="tuto-close"
          onClick={finish}
          aria-label={t("tutorial.fermer")}
          className="absolute top-3 right-3 rounded-lg p-1 text-muted-foreground hover:bg-secondary"
        >
          <X size={17} />
        </button>
        <div className="flex items-center gap-3 pr-6 mb-3">
          <div className="w-10 h-10 shrink-0 rounded-2xl bg-primary/15 text-primary grid place-items-center">
            <Icon size={21} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">
              {i + 1} / {STEPS.length}
            </div>
            <h2 className="font-head text-lg font-bold">{t(S.titleKey)}</h2>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          {t(S.textKey)}
        </p>
        <div className="flex gap-1.5 mb-4">
          {STEPS.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full ${idx <= i ? "bg-primary" : "bg-secondary"}`}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={finish}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {t("tutorial.passer")}
          </button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                data-testid="tuto-previous"
                onClick={() => setI(i - 1)}
                className="rounded-xl border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
              >
                {t("tutorial.precedent")}
              </button>
            )}
            <button
              data-testid={i < STEPS.length - 1 ? "tuto-next" : "tuto-finish"}
              onClick={() => (i < STEPS.length - 1 ? setI(i + 1) : finish())}
              className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {i < STEPS.length - 1
                ? t("alphabetlearnscreen.suivant")
                : t("tutorial.c_est_parti")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
